/**
 * A CONFERÊNCIA DA MAQUININHA, só leitura (fase 6 do espelho, 19/10/2026).
 *
 * O bloco "Conferência com as folhas" do Extrato do Campo do notebook (`ExtratoDoCampo.tsx`): por
 * congregação, no mês, o cartão e o PIX das folhas contra o que a máquina e o PIX depositaram no
 * banco, e a diferença ao centavo, marcada; "confere" quando bate. Por cima da tabela, as frases que
 * o fechamento do Campo do notebook mostra para cada congregação que não confere: no celular elas
 * se leem sem arrastar a tabela para o lado.
 *
 * O notebook confere por congregação e por mês, e não por dia nem por máquina: a máquina serve para
 * dizer de qual congregação é cada crédito. As máquinas de cada congregação vêm no pé.
 */
import type { JSX } from 'react'
import { servidor, type ConferenciaDoCartao as Conferencia, type Eu } from '../api'
import { AtualizadoAs } from '../componentes/AtualizadoAs'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { NavegarMes, useTelaDoMes } from '../componentes/TelaDoMes'
import { emReais } from '../dinheiro'
import { ehParaImprimir, enderecoDaTelaDoCampo, enderecoDoCampo, irPara } from '../rotas'

function Diferenca({ valor }: { valor: number }): JSX.Element {
  return (
    <td className={`valor impresso${valor !== 0 ? ' diferenca' : ''}`}>
      {valor === 0 ? 'confere' : emReais(valor)}
    </td>
  )
}

export function ConferenciaDoCartao({
  eu, competenciaDoEndereco, aoSair
}: {
  eu: Eu
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const paraImprimir = ehParaImprimir()
  const { dados, erro, atualizacao } = useTelaDoMes<Conferencia>(
    competenciaDoEndereco ?? '',
    () => servidor.conferenciaDoCartao(competenciaDoEndereco),
    aoSair,
    paraImprimir
  )
  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDaTelaDoCampo('conferencia-do-cartao', para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: enderecoDoCampo(mes), texto: 'Campo' }} />
      )}
      <main className="folha folha-lancamento tela-da-conferencia" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {!paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            {mes !== null && !paraImprimir && <NavegarMes mes={mes} andar={andar} />}

            <h1>CONFERÊNCIA COM AS FOLHAS – {dados.competencia_por_extenso.toUpperCase()}</h1>
            <p className="explicacao">
              O que a máquina de cartão e o PIX depositaram na conta do Campo, contra as colunas CARTÃO
              e PIX das folhas das congregações. Diferença não impede nada: é para conferir.
            </p>

            {dados.linhas.length === 0 ? (
              <p className="recado aguarde">
                O extrato do banco de {dados.competencia_por_extenso} ainda não tem crédito de cartão,
                PIX ou depósito marcado para uma congregação. Sem extrato não há o que conferir.
              </p>
            ) : (
              <>
                {dados.avisos.length === 0 ? (
                  <p className="tudo-confere">Todas as congregações conferem, no cartão e no PIX.</p>
                ) : (
                  <ul className="avisos-da-conferencia">
                    {dados.avisos.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                )}

                <div className="rola-a-tabela">
                  <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>
                  <table className="grade extrato conferencia">
                    <colgroup><col style={{ width: '22%' }} /><col /><col /><col /><col /><col /><col /><col /></colgroup>
                    <thead>
                      <tr>
                        <th>CONGREGAÇÃO</th>
                        <th className="valor">CARTÃO NAS FOLHAS</th><th className="valor">MÁQUINA NO BANCO</th><th className="valor">DIFERENÇA</th>
                        <th className="valor">PIX NAS FOLHAS</th><th className="valor">PIX NO BANCO</th><th className="valor">DIFERENÇA</th>
                        <th className="valor">DEPÓSITOS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.linhas.map((c) => (
                        <tr key={c.congregacao_id}>
                          <td className="impresso">{c.congregacao}</td>
                          <td className="valor impresso">{emReais(c.cartao_nas_folhas)}</td>
                          <td className="valor impresso">{emReais(c.maquina_no_banco)}</td>
                          <Diferenca valor={c.diferenca_cartao} />
                          <td className="valor impresso">{emReais(c.pix_nas_folhas)}</td>
                          <td className="valor impresso">{emReais(c.pix_no_banco)}</td>
                          <Diferenca valor={c.diferenca_pix} />
                          <td className="valor impresso">{c.deposito_no_banco === 0 ? '' : emReais(c.deposito_no_banco)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="explicacao">
                  A diferença é o banco menos as folhas: positiva quando entrou mais no banco do que as
                  folhas mostram.
                </p>
              </>
            )}

            <h2>As máquinas de cartão de cada congregação</h2>
            {dados.maquinas.length === 0 ? (
              <p className="explicacao">Nenhuma máquina de cartão cadastrada no notebook.</p>
            ) : (
              <table className="grade maquinas-de-cartao">
                <colgroup><col style={{ width: '30%' }} /><col style={{ width: '34%' }} /><col /></colgroup>
                <thead><tr><th>NÚMERO</th><th>CONGREGAÇÃO</th><th>DESCRIÇÃO</th></tr></thead>
                <tbody>
                  {dados.maquinas.map((m) => (
                    <tr key={`${m.congregacao_id}|${m.numero}`}>
                      <td className="impresso">{m.numero}</td>
                      <td className="impresso">{m.congregacao}</td>
                      <td>{m.descricao ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </main>
    </>
  )
}
